import { useState, useEffect, useCallback } from 'react'
import { db } from '../../../firebase'
import { doc, onSnapshot, getDoc } from 'firebase/firestore'

const STALE_AFTER_MS = 60 * 60 * 1000 // 1 hour (matches the shop app)

/**
 * Server-authoritative metal prices. Reads `metal_prices/current` (written by the
 * scheduled Cloud Function from gold-api.com). No hardcoded fallback — when there
 * is no valid price the desk goes "offline" (prices=null) so staff calculate
 * manually, exactly like the original app.
 */
function shape(data) {
  if (!data?.gold?.spotPricePerGram) return null
  const fetchedAt = data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp ? new Date(data.timestamp) : null)
  const ageMs = fetchedAt ? Date.now() - fetchedAt.getTime() : null
  return {
    gold: data.gold.spotPricePerGram,
    silver: data.silver?.spotPricePerGram ?? null,
    goldPerOz: data.gold.spotPricePerOz ?? null,
    source: data.source || 'server',
    fetchedAt,
    ageMinutes: ageMs != null ? Math.floor(ageMs / 60000) : null,
    stale: ageMs != null ? ageMs > STALE_AFTER_MS : false,
  }
}

export function useMetalPrices() {
  const [prices, setPrices] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const ref = doc(db, 'metal_prices', 'current')
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const p = snap.exists() ? shape(snap.data()) : null
        setPrices(p)
        setError(p ? (p.stale ? 'Price is over 1 hour old' : null) : 'No live price — calculate manually')
        setLoading(false)
      },
      (err) => { setError(err?.message || 'Failed to load prices'); setLoading(false) }
    )
    return () => unsub()
  }, [])

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const snap = await getDoc(doc(db, 'metal_prices', 'current'))
      const p = snap.exists() ? shape(snap.data()) : null
      setPrices(p)
      setError(p ? (p.stale ? 'Price is over 1 hour old' : null) : 'No live price — calculate manually')
    } catch (err) {
      setError(err?.message || 'Failed to refresh prices')
    } finally {
      setLoading(false)
    }
  }, [])

  return { prices, loading, error, lastUpdated: prices?.fetchedAt || null, refresh }
}
